import { z } from 'zod';

import { TAX_MODES, WORKSPACE_ROLES } from '../enums';
import { zCurrency, zId, zMoneySigned, zNonEmptyString, zOptionalString, zTaxRate } from './common';

export const createWorkspaceSchema = z.object({
  name: zNonEmptyString(80),
  baseCurrency: zCurrency,
  timezone: z.string().min(1).max(60).default('Europe/Moscow'),
  /** Первый кошелёк создаётся сразу в мастере онбординга. */
  firstWallet: z
    .object({
      name: zNonEmptyString(60),
      type: z.enum(['BANK', 'CARD', 'CASH', 'CRYPTO', 'EWALLET', 'OTHER']).default('BANK'),
      currency: zCurrency,
      openingBalanceMinor: zMoneySigned.default(0),
    })
    .optional(),
});
export type CreateWorkspaceInput = z.infer<typeof createWorkspaceSchema>;

export const updateWorkspaceSchema = z.object({
  name: zNonEmptyString(80).optional(),
  baseCurrency: zCurrency.optional(),
  timezone: z.string().min(1).max(60).optional(),
  weekStart: z.number().int().min(0).max(6).optional(),
  defaultTaxMode: z.enum(TAX_MODES).optional(),
  defaultTaxRate: zTaxRate.optional(),
  reminderDaysBefore: z.array(z.number().int().min(0).max(90)).max(6).optional(),
  overdueReminderEveryDays: z.number().int().min(1).max(30).optional(),
  /**
   * Сколько дней можно отменить выплату или удалить начисление.
   * 0 — без ограничения: закрытый месяц не должен переписываться задним
   * числом, но свежую ошибку человек обязан уметь исправить сам.
   */
  payoutCancelWindowDays: z.number().int().min(0).max(365).optional(),
  separateTaxTransaction: z.boolean().optional(),
  telegramBotToken: zOptionalString(200),
});
export type UpdateWorkspaceInput = z.infer<typeof updateWorkspaceSchema>;

export const workspaceRoleSchema = z.enum(WORKSPACE_ROLES);

export const updateMemberSchema = z.object({
  role: z.enum(['ADMIN', 'MANAGER', 'VIEWER']),
});

export const createInviteSchema = z.object({
  role: z.enum(['ADMIN', 'MANAGER', 'VIEWER']).default('MANAGER'),
  maxUses: z.number().int().min(1).max(50).default(1),
  /**
   * Срок действия ссылки — датой, а не числом дней.
   *
   * Раньше здесь стояло `expiresInDays`, а форма присылала `expiresAt`. Zod
   * молча отбрасывает неизвестные поля, поэтому ссылка выпускалась бессрочной,
   * и никакой ошибки при этом не возникало: человек выбирал дату, а она
   * никуда не доходила. Формат совпадает с приглашениями платформы
   * (`createPlatformInviteSchema`), где с самого начала принималась дата.
   */
  expiresAt: z.string().datetime().nullish(),
});
export type CreateInviteInput = z.infer<typeof createInviteSchema>;

export const joinWorkspaceSchema = z.object({
  code: z.string().trim().min(4).max(40),
});

export const transferOwnershipSchema = z.object({
  newOwnerId: zId,
  /** Подтверждение: пользователь вводит название воркспейса. */
  confirmName: zNonEmptyString(80),
});

/* ─── Налоговые пресеты ─────────────────────────────────────────────────── */

/**
 * Пресетов мы не навязываем: ставку пользователь вводит сам. Но одну и ту же
 * ставку он вводит десятки раз, поэтому её можно сохранить под своим именем.
 */
export const taxPresetSchema = z.object({
  name: zNonEmptyString(40),
  rate: zTaxRate,
  mode: z.enum(TAX_MODES),
  isDefault: z.boolean().default(false),
});
export type TaxPresetInput = z.infer<typeof taxPresetSchema>;

export interface TaxPresetDto {
  id: string;
  name: string;
  rate: number;
  mode: string;
  isDefault: boolean;
}
